/**
 * PersonChooserCard — "you mentioned Sarah — which one?" (PPL-335, UI §1.4).
 *
 * The one write-back card that is genuinely blocking: until the user says who
 * they meant, the item cannot be applied to anybody. Answering calls
 * `respondToSuggestionEurW` with `chosenPersonId`, which both applies the item
 * AND teaches the alias resolver — which is what the reassurance line means
 * literally, not as flattery.
 *
 * Mirrors app/src/components/Writeback/PersonChooserCard.js so the same
 * question looks the same on both surfaces. "Someone new" is offered here
 * because a chooser's mention IS a name (that is why it was ambiguous); the
 * insights queue withholds it for content items, where the mention text is
 * "their father" and would become somebody's first name.
 */

import React, { useState } from 'react';
import { Plus, X } from 'react-feather';
import {
  InlineShell,
  InlineEyebrow,
  CardAvatar,
  CardButton,
  type F8CardData,
  type OnAction,
} from './cardHelpers';

interface Candidate {
  personId: string;
  displayName: string;
  /** The resolver's score for THIS candidate (PPL-454), 0..1 when sent. */
  confidence?: number;
}

export default function PersonChooserCard({
  card,
  onAction,
}: {
  card: F8CardData;
  onAction?: OnAction;
}) {
  const data = card?.data || {};
  const suggestionId: string = data.suggestionId || '';
  const mention: string = data.mentionText || 'someone';
  const contextHint: string = data.contextHint || '';
  const candidates: Candidate[] = Array.isArray(data.candidates) ? data.candidates : [];

  const [busy, setBusy] = useState<string | null>(null);
  const [settled, setSettled] = useState<'chosen' | 'skipped' | 'stale' | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Cards live in the chat thread forever, so this one can be tapped long
   * after the same question was answered in the review queue (or on another
   * device). The server says so rather than failing — `already_resolved` — and
   * a stale tap must read as "that's handled", not as an error.
   */
  const settleFrom = (res: any, chosen: 'chosen' | 'skipped') =>
    res?.data?.status === 'already_resolved' ? 'stale' : chosen;

  const respond = async (chosenPersonId: string, label: string) => {
    if (!suggestionId || busy) return;
    setBusy(label);
    setError(null);
    try {
      const res = await onAction?.({
        action: 'cloud_function',
        params: {
          functionName: 'respondToSuggestionEurW',
          args: { suggestionId, action: 'accept', chosenPersonId },
        },
      });
      setSettled(settleFrom(res, 'chosen'));
    } catch {
      setError("couldn't save that — try again");
    } finally {
      setBusy(null);
    }
  };

  const skip = async () => {
    if (!suggestionId || busy) return;
    setBusy('skip');
    setError(null);
    try {
      const res = await onAction?.({
        action: 'cloud_function',
        params: {
          functionName: 'respondToSuggestionEurW',
          args: { suggestionId, action: 'reject' },
        },
      });
      setSettled(settleFrom(res, 'skipped'));
    } catch {
      setError("couldn't do that — try again");
    } finally {
      setBusy(null);
    }
  };

  if (settled) {
    return (
      <InlineShell>
        <div style={{ fontSize: 13, color: 'rgba(240,232,250,0.82)' }}>
          {settled === 'stale'
            ? 'already sorted — nothing more to do here'
            : settled === 'chosen'
              ? "got it — i'll remember that for next time"
              : 'no problem — left it alone'}
        </div>
      </InlineShell>
    );
  }

  return (
    <InlineShell>
      <InlineEyebrow label="which one?" />
      <div style={{ fontSize: 14.5, fontWeight: 600, color: '#fff', letterSpacing: '-0.01em' }}>
        you mentioned {mention} — which one?
      </div>

      {contextHint ? (
        <div
          style={{
            marginTop: 8,
            paddingLeft: 10,
            borderLeft: '2px solid rgba(217,70,239,0.35)',
            fontSize: 12.5,
            lineHeight: 1.5,
            fontStyle: 'italic',
            color: 'rgba(232,220,250,0.7)',
          }}
        >
          “{contextHint}”
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
        {candidates.map((c) => (
          <button
            key={c.personId}
            type="button"
            disabled={!!busy}
            onClick={() => void respond(c.personId, c.personId)}
            style={{
              appearance: 'none',
              cursor: busy ? 'default' : 'pointer',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 12,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff',
              textAlign: 'left',
              opacity: busy && busy !== c.personId ? 0.5 : 1,
            }}
          >
            <CardAvatar name={c.displayName} size={26} />
            <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600 }}>
              {c.displayName}
            </span>
            {typeof c.confidence === 'number' && Number.isFinite(c.confidence) ? (
              <span style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(220,200,240,0.5)', fontVariantNumeric: 'tabular-nums' }}>
                {Math.max(0, Math.min(100, Math.round(c.confidence * 100)))}%
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 7, marginTop: 10, flexWrap: 'wrap' }}>
        {/* A chooser's mention IS a name, so "someone new" is safe here. */}
        <CardButton small disabled={!!busy} onClick={() => void respond('new', 'new')}>
          <Plus size={12} /> someone new
        </CardButton>
        <CardButton small disabled={!!busy} onClick={() => void skip()}>
          <X size={12} /> skip
        </CardButton>
      </div>

      <div style={{ fontSize: 11.5, color: 'rgba(220,200,240,0.5)', marginTop: 9 }}>
        i&apos;ll remember this for next time
      </div>

      {error ? (
        <div style={{ fontSize: 12, color: '#fca5a5', marginTop: 8 }}>{error}</div>
      ) : null}
    </InlineShell>
  );
}
